import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import Reveal from "@/components/marketing/Reveal";
import { useAuth } from "@/context/AuthContext";

const included = [
  "Multi-venue booking calendar",
  "Confirmed & tentative bookings",
  "Automatic revenue this month & FY-to-date",
  "Profit & Loss per venue with analytics",
  "Owner analytics: charts, occupancy & bookers",
  "Unlimited employee accounts — no extra cost",
  "Branded bookings sheet & CSV export",
  "Installable app (Add to Home Screen)",
];

const PLANS = {
  monthly: { label: "Monthly", price: "₹1,000", per: "/ month", note: "Billed monthly", badge: null, sub: "Flexible, cancel anytime" },
  yearly: { label: "Yearly", price: "₹840", per: "/ month", note: "Billed ₹10,080 once a year", badge: "Save 16%", sub: "Best value — one annual payment" },
};

export default function Pricing() {
  const [billing, setBilling] = useState("monthly");
  const [venueCount, setVenueCount] = useState(1);
  const navigate = useNavigate();
  const { user } = useAuth();
  const plan = PLANS[billing];

  const startTrial = () => {
    try {
      localStorage.setItem("pulse_intended_venues", String(venueCount));
      localStorage.setItem("pulse_intended_billing", billing);
    } catch (e) {}
    navigate(user ? "/dashboard" : "/login");
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-16 sm:px-8 sm:py-24">
      <Reveal>
        <div className="text-center">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-slate-200 bg-cloud px-3.5 py-1.5 text-xs font-medium text-slate">
            <Sparkles size={13} className="text-pulse-green" /> Simple, honest pricing
          </div>
          <h1 className="mt-6 text-5xl font-bold tracking-tight text-pulse-navy md:text-6xl">One plan. Everything in.</h1>
          <p className="mx-auto mt-5 max-w-xl text-lg text-slate">Full access to the Calendar product — no tiers, no per-seat fees. Employee accounts are always free.</p>
        </div>
      </Reveal>

      {/* Billing toggle */}
      <Reveal delay={0.05}>
        <div className="mx-auto mt-10 flex w-fit items-center gap-1 rounded-full border border-slate-200 bg-white p-1" data-testid="billing-toggle">
          {["monthly", "yearly"].map((b) => (
            <button key={b} onClick={() => setBilling(b)} data-testid={`billing-${b}`}
              className={`relative rounded-full px-5 py-2 text-sm font-semibold transition-colors ${billing === b ? "bg-pulse-navy text-white" : "text-slate hover:text-pulse-navy"}`}>
              {PLANS[b].label}
              {b === "yearly" && <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${billing === b ? "bg-pulse-green/25 text-pulse-green" : "bg-pulse-green/15 text-pulse-green"}`}>Save 16%</span>}
            </button>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mx-auto mt-8 max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white shadow-pulse-lg" data-testid="pricing-card">
          <div className="relative bg-pulse-navy p-8 text-white grain-overlay">
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-white/60">Pulse — Calendar</div>
                {plan.badge && <span className="rounded-md bg-pulse-green/20 px-2.5 py-1 text-xs font-semibold text-pulse-green">{plan.badge}</span>}
              </div>
              <div className="mt-3 flex items-end gap-2">
                <span className="text-5xl font-bold" data-testid="plan-price">{plan.price}</span>
                <span className="mb-1.5 text-white/60">{plan.per}</span>
              </div>
              <div className="mt-1 text-sm text-white/50" data-testid="plan-note">{plan.note}</div>
              <div className="mt-2 text-xs text-white/50" data-testid="venue-addon-note">Includes 1 venue · each additional venue {billing === "yearly" ? "+₹2,000/year" : "+₹200/month"}</div>
              <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-1.5 text-sm font-medium text-white/80">
                15-day free trial · no card required
              </div>
            </div>
          </div>
          <div className="p-8">
            <ul className="space-y-3.5">
              {included.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-charcoal">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-pulse-green/10 text-pulse-green"><Check size={13} /></span>{f}
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-lg border border-slate-200 p-4" data-testid="venue-calculator">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-pulse-navy">How many venues?</span>
                <div className="flex items-center gap-3">
                  <button onClick={() => setVenueCount((v) => Math.max(1, v - 1))} data-testid="venue-minus" aria-label="Fewer venues" className="grid h-8 w-8 place-items-center rounded-md border border-slate-200 text-lg leading-none text-pulse-navy transition-colors hover:bg-cloud">−</button>
                  <span className="w-6 text-center font-semibold text-pulse-navy" data-testid="venue-count">{venueCount}</span>
                  <button onClick={() => setVenueCount((v) => v + 1)} data-testid="venue-plus" aria-label="More venues" className="grid h-8 w-8 place-items-center rounded-md border border-slate-200 text-lg leading-none text-pulse-navy transition-colors hover:bg-cloud">+</button>
                </div>
              </div>
              {venueCount > 1 && (
                <p className="mt-2 text-xs text-slate">{venueCount - 1} extra venue{venueCount - 1 === 1 ? "" : "s"} × {billing === "yearly" ? "₹2,000/year" : "₹200/month"} = <b className="text-pulse-navy">+₹{((venueCount - 1) * (billing === "yearly" ? 2000 : 200)).toLocaleString("en-IN")}{billing === "yearly" ? "/year" : "/month"}</b></p>
              )}
              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                <span className="text-sm text-slate">{billing === "yearly" ? "Total per year" : "Total per month"}</span>
                <span className="text-lg font-bold text-pulse-navy" data-testid="venue-total">
                  {billing === "yearly"
                    ? `₹${(10080 + (venueCount - 1) * 2000).toLocaleString("en-IN")}/yr`
                    : `₹${(1000 + (venueCount - 1) * 200).toLocaleString("en-IN")}/mo`}
                </span>
              </div>
            </div>
            <button onClick={startTrial} data-testid="pricing-start-trial-btn"
              className="group mt-8 flex w-full items-center justify-center gap-2 rounded-md bg-pulse-green px-6 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.01] active:scale-[0.98]">
              Start your 15-day free trial <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="mt-4 text-center text-xs text-slate">{plan.sub} · your trial activates instantly the moment you sign in — no waiting.</p>
            <p className="mt-3 text-center text-[11px] leading-relaxed text-slate/70">Price includes <b className="text-slate">1 venue</b>. When you purchase you can add more venues at <b className="text-slate">₹200/venue per month</b> (monthly) or <b className="text-slate">₹2,000/venue per year</b> (yearly). This plan covers the Calendar product; new Pulse products launched later will be sold separately.</p>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
